"use client";

import { useEffect, useSyncExternalStore } from "react";
import {
  type MemoContextKeyInput,
  memoContextKey,
} from "@/app/[lang]/[workspaceId]/_components/layout/memo-node-id";
import type { UserMemoContext } from "@/app/[lang]/[workspaceId]/_components/layout/workspace-side-panel-mode";

const MEMO_CREATE_TARGET_OPTIONS_EVENT =
  "amethyst:user-memo-create-target-options";

type UserMemoCreateTargetHost = Pick<
  MemoContextKeyInput,
  "workspaceId" | "projectId" | "featureKind"
>;

export type UserMemoCreateTargetOption = {
  key: string;
  label: string;
  description?: string;
  context: UserMemoContext;
};

const cachedCreateTargetOptionsByHost = new Map<
  string,
  readonly UserMemoCreateTargetOption[]
>();
const EMPTY_CREATE_TARGET_OPTIONS: readonly UserMemoCreateTargetOption[] = [];

function userMemoCreateTargetHostKey(host: UserMemoCreateTargetHost) {
  return [host.workspaceId, host.projectId, host.featureKind].join("\u001f");
}

function emitUserMemoCreateTargetOptionsChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(MEMO_CREATE_TARGET_OPTIONS_EVENT));
}

function subscribeUserMemoCreateTargetOptionsChanged(
  onStoreChange: () => void,
) {
  if (typeof window === "undefined") return () => undefined;

  window.addEventListener(MEMO_CREATE_TARGET_OPTIONS_EVENT, onStoreChange);
  return () => {
    window.removeEventListener(MEMO_CREATE_TARGET_OPTIONS_EVENT, onStoreChange);
  };
}

function createTargetOptionWithStableKey(
  option: Omit<UserMemoCreateTargetOption, "key">,
): UserMemoCreateTargetOption {
  return {
    ...option,
    key: memoContextKey(option.context),
  };
}

/**
 * 一覧画面で選択できる memo 作成 target を外部 store へ公開します。
 * side panel は layout 配下にいるため、board card 側が描画している view 一覧をこの境界で受け渡します。
 */
export function usePublishUserMemoCreateTargetOptions(
  host: UserMemoCreateTargetHost | null,
  options: readonly Omit<UserMemoCreateTargetOption, "key">[] | null,
) {
  useEffect(() => {
    if (!host || !options) return;

    const hostKey = userMemoCreateTargetHostKey(host);
    cachedCreateTargetOptionsByHost.set(
      hostKey,
      options.map(createTargetOptionWithStableKey),
    );
    emitUserMemoCreateTargetOptionsChanged();

    return () => {
      cachedCreateTargetOptionsByHost.delete(hostKey);
      emitUserMemoCreateTargetOptionsChanged();
    };
  }, [host, options]);
}

/**
 * 現在の board/list route に対応する memo 作成 target 候補を読みます。
 * 候補が未公開の route では空配列を返し、既存の intent context 作成にフォールバックさせます。
 */
export function useUserMemoCreateTargetOptions(
  host: UserMemoCreateTargetHost | null,
) {
  return useSyncExternalStore(
    subscribeUserMemoCreateTargetOptionsChanged,
    () => readUserMemoCreateTargetOptions(host),
    () => EMPTY_CREATE_TARGET_OPTIONS,
  );
}

/** テストと controller の同期処理向けに、現在の作成 target 候補を直接読みます。 */
function readUserMemoCreateTargetOptions(
  host: UserMemoCreateTargetHost | null,
): readonly UserMemoCreateTargetOption[] {
  if (!host) return EMPTY_CREATE_TARGET_OPTIONS;
  return (
    cachedCreateTargetOptionsByHost.get(userMemoCreateTargetHostKey(host)) ??
    EMPTY_CREATE_TARGET_OPTIONS
  );
}

/** テストで route host ごとの候補を明示的に seed / cleanup します。 */
export function writeUserMemoCreateTargetOptions(
  host: UserMemoCreateTargetHost,
  options: readonly Omit<UserMemoCreateTargetOption, "key">[] | null,
) {
  const hostKey = userMemoCreateTargetHostKey(host);
  if (options) {
    cachedCreateTargetOptionsByHost.set(
      hostKey,
      options.map(createTargetOptionWithStableKey),
    );
  } else {
    cachedCreateTargetOptionsByHost.delete(hostKey);
  }
  emitUserMemoCreateTargetOptionsChanged();
}
